import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Space, Tag, theme, Table, Card, Row, Col } from 'antd';
import { DishCatalogDto, CatalogSource } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { LocalizedContentShow } from '../../components/LocalizedContentTabs';

export const DishCatalogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<DishCatalogDto>({ resource: 'dish-catalog' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="dish-catalog">
      <div style={{ padding: '8px 0' }}>
        <LocalizedContentShow
          record={item}
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Field label="Aliases">
                {(item.aliases || []).length > 0 ? (
                  <Space wrap>
                    {item.aliases.map((a) => (
                      <Tag key={a} style={{ borderRadius: 9999, padding: '2px 10px' }}>{a}</Tag>
                    ))}
                  </Space>
                ) : '—'}
              </Field>
              <Field label="Type">
                <Tag style={{ borderRadius: 9999, padding: '2px 10px' }}>{item.type || '—'}</Tag>
              </Field>
              <Field label="Category">
                <Tag style={{ borderRadius: 9999, padding: '2px 10px' }}>{item.category}</Tag>
              </Field>
              <Field label="Standard Portion">{item.standardPortionGrams} g</Field>
              <Field label="Active">
                <Tag
                  style={{
                    borderColor: item.isActive ? '#3ecf8e' : '#898989',
                    color: item.isActive ? '#3ecf8e' : '#898989',
                    background: item.isActive ? '#3ecf8e15' : '#89898915',
                    borderRadius: 9999,
                    padding: '2px 12px',
                    fontWeight: 500,
                  }}
                >
                  {item.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </Field>
              <Field label="Source">
                <Tag
                  style={{
                    borderColor: item.source === CatalogSource.USDA ? '#52c41a' : item.source === CatalogSource.CFS ? '#1890ff' : '#d9d9d9',
                    color: item.source === CatalogSource.USDA ? '#52c41a' : item.source === CatalogSource.CFS ? '#1890ff' : '#666',
                    borderRadius: 9999,
                    padding: '2px 12px',
                    fontWeight: 500,
                  }}
                >
                  {item.source || CatalogSource.MANUAL}
                </Tag>
              </Field>
            </Col>
            <Col span={12}>
              <Field label="Calories">{item.calories} kcal</Field>
              <Field label="Protein">{item.protein} g</Field>
              <Field label="Fat">{item.fat} g</Field>
              <Field label="Carbs">{item.carbs} g</Field>
              <Field label="Fiber">{item.fiber != null ? `${item.fiber} g` : '—'}</Field>
              <Field label="Sodium">{item.sodium != null ? `${item.sodium} mg` : '—'}</Field>
            </Col>
          </Row>

          <Card title="Ingredients" size="small" style={{ marginTop: 24 }}>
            <Table
              dataSource={item.ingredients || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: 'Display Name', dataIndex: 'displayName', width: 200 },
                { title: 'Weight (g)', dataIndex: 'weightGrams', width: 100 },
                { title: '%', dataIndex: 'percentage', width: 80, render: (v: number) => `${Math.round(v * 100)}%` },
                { title: 'Catalog ID', dataIndex: 'ingredientCatalogId', width: 280, render: (v: string | null) => v || '—' },
              ]}
            />
          </Card>

          <Card title="Variations" size="small" style={{ marginTop: 16 }}>
            <Table
              dataSource={item.variations || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: 'Name', dataIndex: 'name', width: 200 },
                { title: 'Δ Calories', dataIndex: 'deltaCalories', width: 100 },
                { title: 'Δ Protein', dataIndex: 'deltaProtein', width: 100, render: (v: number) => v || '—' },
                { title: 'Δ Fat', dataIndex: 'deltaFat', width: 100, render: (v: number) => v || '—' },
                { title: 'Δ Carbs', dataIndex: 'deltaCarbs', width: 100, render: (v: number) => v || '—' },
                { title: 'Δ Fiber', dataIndex: 'deltaFiber', width: 100, render: (v: number) => v || '—' },
                { title: 'Δ Sodium', dataIndex: 'deltaSodium', width: 100, render: (v: number) => v || '—' },
              ]}
            />
          </Card>

          <div style={{ marginTop: 16 }}>
            <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
            <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
          </div>
        </div>
      </div>
    </Show>
  );
};
